import React, { useState, useEffect } from "react";
import {
  Search,
  Edit,
  Trash2,
  ChevronLeft,
  ChevronRight,
  X,
  Save
} from "lucide-react";
import { useTheme } from "../../../../context/ThemeContext";
import { getAllUsers, adminDeleteUser, adminEditUser } from "../../../api";

const Users = () => {
  const { isDarkTheme } = useTheme();
  const [searchTerm, setSearchTerm] = useState("");
  const [searchType, setSearchType] = useState("id"); // "id" or "account"
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(10);

  // Modal states
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  // Edit states
  const [editMode, setEditMode] = useState(false);
  const [userToEdit, setUserToEdit] = useState(null);
  const [editFormData, setEditFormData] = useState({
    name: "",
    // Email is not editable now, so it's removed from the edit form
  });

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const data = await getAllUsers();
        setUsers(data.users_list);
      } catch (err) {
        setError("Failed to fetch users");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  const filteredUsers = users.filter((user) =>
    searchType === "id"
      ? user.id.toString().includes(searchTerm)
      : user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  // Delete functions
  const openDeleteModal = (user) => {
    setUserToDelete(user);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setUserToDelete(null);
  };

  const confirmDelete = async () => {
    if (userToDelete) {
      try {
        const response = await adminDeleteUser(userToDelete.id);
        if (response.status === "success") {
          const updatedUsers = users.filter(user => user.id !== userToDelete.id);
          setUsers(updatedUsers);
          closeDeleteModal();
        } else {
          console.error("Delete failed:", response.message || "Unknown error");
        }
      } catch (error) {
        console.error("Failed to delete user: ", userToDelete.id, " Error: ", error);
      }
    }
  };

  // Edit functions
  const startEdit = (user) => {
    setUserToEdit(user);
    setEditFormData({
      name: user.name,
    });
    setEditMode(true);
  };

  const cancelEdit = () => {
    setUserToEdit(null);
    setEditMode(false);
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const saveEdit = async () => {
    if (userToEdit) {
      try {
        const response = await adminEditUser(userToEdit.id, editFormData.name);
        if (response.status === "success") {
          const updatedUsers = users.map(user => {
            if (user.id === userToEdit.id) {
              return {
                ...user,
                name: editFormData.name
              };
            }
            return user;
          });
          setUsers(updatedUsers);
          cancelEdit();
        } else {
          console.error("Edit failed:", response.message || "Unknown error");
          // Optionally show error to user
        }
      } catch (error) {
        console.error("Failed to update user:", error);
        // Optionally show error to user
      }
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between md:items-center gap-4">
        <div>
          <h1 className={`text-2xl font-bold bg-gradient-to-r from-amber-500 to-amber-600 bg-clip-text text-transparent`}>User Management</h1>
          <p className={`text-sm mt-1 ${isDarkTheme ? "text-gray-400" : "text-gray-500"}`}>
            Manage user accounts and track activity
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <div className={`relative w-full sm:w-80 ${isDarkTheme ? 'text-white' : 'text-gray-900'}`}>
            <input
              type="text"
              placeholder={`Search by ${searchType === "id" ? "ID" : "name or email"}...`}
              className={`pl-10 pr-4 py-2 w-full rounded-lg border ${isDarkTheme
                ? "bg-gray-800 border-gray-700 text-white"
                : "bg-white border-gray-300"
                } focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all duration-200`}
              value={searchTerm}
              onChange={handleSearchChange}
            />
            <Search
              className="absolute left-3 top-2.5 text-gray-400"
              size={18}
            />
          </div>

          <div className="relative w-full md:w-52">
            <select
              className={`w-full py-2 px-3 pr-8 rounded-lg border appearance-none ${isDarkTheme
                ? "bg-gray-800 border-gray-700 text-white"
                : "bg-white border-gray-300 text-gray-900"
                } focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all duration-200`}
              value={searchType}
              onChange={(e) => {
                setSearchType(e.target.value);
                setSearchTerm("");
              }}
            >
              <option value="id">Search by ID</option>
              <option value="account">Search by Account</option>
            </select>
            <svg
              className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none"
              width="20"
              height="20"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.23 8.29a.75.75 0 010-1.08z"
                clipRule="evenodd"
              />
            </svg>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className={`overflow-x-auto rounded-lg border ${isDarkTheme ? "border-gray-700" : "border-gray-200"}`}>
        <table className={`min-w-full divide-y ${isDarkTheme ? "divide-gray-700 bg-gray-800" : "divide-gray-200 bg-white"}`}>
          <thead className={isDarkTheme ? "bg-gray-900" : "bg-gray-50"}>
            <tr>
              <th
                scope="col"
                className={`px-6 py-3 text-left text-xs font-medium ${isDarkTheme ? "text-gray-400" : "text-gray-500"} uppercase tracking-wider`}
              >
                ID
              </th>
              <th
                scope="col"
                className={`px-6 py-3 text-left text-xs font-medium ${isDarkTheme ? "text-gray-400" : "text-gray-500"} uppercase tracking-wider`}
              >
                Name
              </th>
              <th
                scope="col"
                className={`px-6 py-3 text-left text-xs font-medium ${isDarkTheme ? "text-gray-400" : "text-gray-500"} uppercase tracking-wider`}
              >
                Email
              </th>
              <th
                scope="col"
                className={`px-6 py-3 text-left text-xs font-medium ${isDarkTheme ? "text-gray-400" : "text-gray-500"} uppercase tracking-wider`}
              >
                Last Active
              </th>
              <th
                scope="col"
                className={`px-6 py-3 text-right text-xs font-medium ${isDarkTheme ? "text-gray-400" : "text-gray-500"} uppercase tracking-wider`}
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody className={`divide-y ${isDarkTheme ? "divide-gray-700" : "divide-gray-200"}`}>
            {currentUsers.map((user) => (
              <tr key={user.id} className={`${isDarkTheme ? "hover:bg-gray-700" : "hover:bg-gray-50"} transition-colors duration-150`}>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  #{user.id}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className={`h-9 w-9 rounded-full bg-amber-500 flex items-center justify-center text-white font-medium`}>
                      {editMode && userToEdit && userToEdit.id === user.id ? editFormData.name.charAt(0) : user.name.charAt(0)}
                    </div>
                    <div className="ml-3">
                      {editMode && userToEdit && userToEdit.id === user.id ? (
                        <input
                          type="text"
                          name="name"
                          value={editFormData.name}
                          onChange={handleEditFormChange}
                          className={`w-full px-2 py-1 text-sm rounded border ${isDarkTheme
                              ? "bg-gray-700 border-gray-600 text-white"
                              : "bg-white border-gray-300 text-gray-900"
                            } focus:outline-none focus:ring-2 focus:ring-amber-500`}
                        />
                      ) : (
                        <div className={`text-sm font-medium ${isDarkTheme ? "text-white" : "text-gray-900"}`}>
                          {user.name}
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className={`text-sm ${isDarkTheme ? "text-gray-300" : "text-gray-500"}`}>
                    {user.email}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className={`text-sm ${isDarkTheme ? "text-gray-300" : "text-gray-500"}`}>
                    {user.lastActive}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                  {editMode && userToEdit && userToEdit.id === user.id ? (
                    <div className="flex justify-end">
                      <button
                        onClick={saveEdit}
                        className={`p-1 rounded-md ${isDarkTheme ? "hover:bg-gray-700" : "hover:bg-gray-100"} transition-colors duration-150`}
                      >
                        <Save size={18} className="text-amber-500" />
                      </button>
                      <button
                        onClick={cancelEdit}
                        className={`p-1 rounded-md ${isDarkTheme ? "hover:bg-gray-700" : "hover:bg-gray-100"} transition-colors duration-150`}
                      >
                        <X size={18} className="text-gray-500" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex justify-end">
                      <button
                        onClick={() => startEdit(user)}
                        className={`p-1 rounded-md ${isDarkTheme ? "hover:bg-gray-700" : "hover:bg-gray-100"} transition-colors duration-150`}
                      >
                        <Edit size={18} className="text-amber-500" />
                      </button>
                      <button
                        onClick={() => openDeleteModal(user)}
                        className={`p-1 rounded-md ${isDarkTheme ? "hover:bg-gray-700" : "hover:bg-gray-100"} transition-colors duration-150`}
                      >
                        <Trash2 size={18} className="text-red-500" />
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex justify-between items-center">
        <div className={`text-sm ${isDarkTheme ? "text-gray-400" : "text-gray-500"}`}>
          Showing {indexOfFirstUser + 1}-{Math.min(indexOfLastUser, filteredUsers.length)} of {filteredUsers.length} users
        </div>

        <div className="flex items-center space-x-2">
          <button
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className={`p-2 rounded-md ${currentPage === 1
              ? isDarkTheme
                ? "text-gray-600 bg-gray-800"
                : "text-gray-400 bg-gray-100"
              : isDarkTheme
                ? "text-white hover:bg-gray-700"
                : "text-gray-700 hover:bg-gray-100"
              } transition-colors duration-150`}
          >
            <ChevronLeft size={18} />
          </button>

          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <button
              key={page}
              onClick={() => setCurrentPage(page)}
              className={`px-3 py-1 rounded-md ${currentPage === page
                ? "bg-amber-500 text-white"
                : isDarkTheme
                  ? "text-white hover:bg-gray-700"
                  : "text-gray-700 hover:bg-gray-100"
                } transition-colors duration-150`}
            >
              {page}
            </button>
          ))}

          <button
            onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className={`p-2 rounded-md ${currentPage === totalPages
              ? isDarkTheme
                ? "text-gray-600 bg-gray-800"
                : "text-gray-400 bg-gray-100"
              : isDarkTheme
                ? "text-white hover:bg-gray-700"
                : "text-gray-700 hover:bg-gray-100"
              } transition-colors duration-150`}
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" aria-hidden="true" />
          <div className="flex items-center justify-center min-h-screen p-4">
            <div className={`relative w-full max-w-md p-6 rounded-lg shadow-lg ${isDarkTheme ? "bg-gray-800" : "bg-white"}`}>
              <div className="flex justify-between items-center mb-4">
                <h3 className={`text-lg font-medium ${isDarkTheme ? "text-white" : "text-gray-900"}`}>
                  Confirm Delete
                </h3>
                <button
                  onClick={closeDeleteModal}
                  className={`p-1 rounded-md ${isDarkTheme ? "hover:bg-gray-700" : "hover:bg-gray-100"} transition-colors duration-150`}
                >
                  <X size={20} className={isDarkTheme ? "text-gray-400" : "text-gray-500"} />
                </button>
              </div>
              <div className="mb-6">
                <p className={`mb-2 ${isDarkTheme ? "text-gray-300" : "text-gray-500"}`}>
                  Are you sure you want to delete this user?
                </p>
                <div className={`p-4 rounded-lg ${isDarkTheme ? "bg-gray-700" : "bg-gray-50"}`}>
                  <div className="flex items-center">
                    <div className={`h-10 w-10 rounded-full bg-amber-500 flex items-center justify-center text-white font-medium`}>
                      {userToDelete?.name.charAt(0)}
                    </div>
                    <div className="ml-3">
                      <div className={`font-medium ${isDarkTheme ? "text-white" : "text-gray-900"}`}>
                        {userToDelete?.name}
                      </div>
                      <div className={`text-sm ${isDarkTheme ? "text-gray-400" : "text-gray-500"}`}>
                        {userToDelete?.email}
                      </div>
                    </div>
                  </div>
                </div>
                <p className={`mt-3 text-sm ${isDarkTheme ? "text-red-400" : "text-red-600"}`}>
                  This action cannot be undone.
                </p>
              </div>
              <div className="flex justify-end gap-3">
                <button
                  onClick={closeDeleteModal}
                  className={`px-4 py-2 text-sm font-medium rounded-md ${isDarkTheme
                      ? "bg-gray-700 text-white hover:bg-gray-600"
                      : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                    } transition-colors`}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors"
                >
                  Delete User
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
